import { useState, useEffect, createContext, useContext, ReactNode, useMemo, useCallback } from 'react';
import { User, Session } from '@supabase/supabase-js';
import { supabase } from '@/integrations/supabase/client';

type AppRole = 'admin' | 'user' | 'editor' | 'packet_reviewer' | 'customer_service';

interface AuthContextType {
  user: User | null;
  session: Session | null;
  isAdmin: boolean;
  isAffiliate: boolean;
  adminCheckComplete: boolean;
  loading: boolean;
  signIn: (email: string, password: string) => Promise<{ error: Error | null }>;
  signUp: (email: string, password: string, fullName?: string) => Promise<{ error: Error | null }>;
  signOut: () => Promise<void>;
  resetPassword: (email: string) => Promise<{ error: Error | null }>;
  updatePassword: (password: string) => Promise<{ error: Error | null }>;
  signInWithGoogle: () => Promise<{ error: Error | null }>;
  hasRole: (role: AppRole) => Promise<boolean>;
  getUserRoles: () => Promise<AppRole[]>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

// Session storage caching for role checks (5 min expiry)
const CACHE_EXPIRY_MS = 5 * 60 * 1000;

const getCachedValue = <T,>(key: string): T | null => {
  try {
    const cached = sessionStorage.getItem(key);
    if (cached) {
      const parsed = JSON.parse(cached);
      const { value, expiry } = parsed;
      if (Date.now() < expiry) return value as T;
      sessionStorage.removeItem(key);
    }
  } catch (error) {
    console.error('Cache read error:', error);
  }
  return null;
};

const setCachedValue = <T,>(key: string, value: T): void => {
  try {
    sessionStorage.setItem(key, JSON.stringify({
      value,
      expiry: Date.now() + CACHE_EXPIRY_MS,
    }));
  } catch (error) {
    console.error('Cache write error:', error);
  }
};

const clearUserCache = (userId: string): void => {
  try {
    sessionStorage.removeItem(`admin-role-${userId}`);
    sessionStorage.removeItem(`affiliate-status-${userId}`);
  } catch (error) {
    console.error('Cache clear error:', error);
  }
};

export const AuthProvider = ({ children }: { children: ReactNode }) => {
  const [user, setUser] = useState<User | null>(null);
  const [session, setSession] = useState<Session | null>(null);
  const [isAdmin, setIsAdmin] = useState(false);
  const [isAffiliate, setIsAffiliate] = useState(false);
  const [adminCheckComplete, setAdminCheckComplete] = useState(false);
  const [loading, setLoading] = useState(true);

  const checkAdminRole = async (userId: string): Promise<boolean> => {
    const cacheKey = `admin-role-${userId}`;
    const cached = getCachedValue<boolean>(cacheKey);
    if (cached !== null) return cached;

    try {
      const { data, error } = await supabase
        .from('user_roles')
        .select('role')
        .eq('user_id', userId)
        .eq('role', 'admin')
        .maybeSingle();
      
      if (error) {
        console.error('Error checking admin role:', error);
        return false;
      }
      const result = !!data;
      setCachedValue(cacheKey, result);
      return result;
    } catch (err) {
      console.error('Error in checkAdminRole:', err);
      return false;
    }
  };

  const checkAffiliateStatus = async (userId: string): Promise<boolean> => {
    const cacheKey = `affiliate-status-${userId}`;
    const cached = getCachedValue<boolean>(cacheKey);
    if (cached !== null) return cached;

    try {
      const { data, error } = await supabase
        .from('affiliates')
        .select('id, status')
        .eq('user_id', userId)
        .eq('status', 'approved')
        .maybeSingle();
      
      if (error) {
        console.error('Error checking affiliate status:', error);
        return false;
      }
      const result = !!data;
      setCachedValue(cacheKey, result);
      return result;
    } catch (err) {
      console.error('Error in checkAffiliateStatus:', err);
      return false;
    }
  };

  const hasRole = async (role: AppRole): Promise<boolean> => {
    if (!user) return false;
    try {
      const { data, error } = await supabase
        .from('user_roles')
        .select('role')
        .eq('user_id', user.id)
        .eq('role', role)
        .maybeSingle();
      
      if (error) {
        console.error('Error checking role:', error);
        return false;
      }
      return !!data;
    } catch (err) {
      console.error('Error in hasRole:', err);
      return false;
    }
  };

  const getUserRoles = async (): Promise<AppRole[]> => {
    if (!user) return [];
    try {
      const { data, error } = await supabase
        .from('user_roles')
        .select('role')
        .eq('user_id', user.id);
      
      if (error) {
        console.error('Error getting user roles:', error);
        return [];
      }
      return (data || []).map(r => r.role as AppRole);
    } catch (err) {
      console.error('Error in getUserRoles:', err);
      return [];
    }
  };

  useEffect(() => {
    // Set up auth state listener FIRST
    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      (event, session) => {
        setSession(session);
        setUser(session?.user ?? null);
        
        // Defer admin/affiliate check with setTimeout to prevent deadlock
        if (session?.user) {
          setAdminCheckComplete(false);
          setTimeout(async () => {
            try {
              const [adminResult, affiliateResult] = await Promise.all([
                checkAdminRole(session.user.id),
                checkAffiliateStatus(session.user.id)
              ]);
              setIsAdmin(adminResult);
              setIsAffiliate(affiliateResult);
              setAdminCheckComplete(true);
            } catch (error) {
              console.error('Failed to check user roles:', error);
              setAdminCheckComplete(true);
            }
          }, 0);
        } else {
          setIsAdmin(false);
          setIsAffiliate(false);
          setAdminCheckComplete(true);
        }
      }
    );

    // THEN check for existing session
    supabase.auth.getSession().then(async ({ data: { session } }) => {
      setSession(session);
      setUser(session?.user ?? null);
      
      if (session?.user) {
        try {
          const [adminResult, affiliateResult] = await Promise.all([
            checkAdminRole(session.user.id),
            checkAffiliateStatus(session.user.id)
          ]);
          setIsAdmin(adminResult);
          setIsAffiliate(affiliateResult);
          setAdminCheckComplete(true);
        } catch (error) {
          console.error('Failed to check user roles on init:', error);
          setAdminCheckComplete(true);
        }
      } else {
        setAdminCheckComplete(true);
      }
      setLoading(false);
    });

    return () => subscription.unsubscribe();
  }, []);

  const signIn = async (email: string, password: string) => {
    const { error } = await supabase.auth.signInWithPassword({
      email,
      password,
    });
    return { error };
  };

  const signUp = async (email: string, password: string, fullName?: string) => {
    const redirectUrl = `${window.location.origin}/`;
    
    const { error } = await supabase.auth.signUp({
      email,
      password,
      options: {
        emailRedirectTo: redirectUrl,
        data: {
          full_name: fullName,
        },
      },
    });
    return { error };
  };

  const signOut = async () => {
    if (user) {
      clearUserCache(user.id);
    }
    await supabase.auth.signOut();
    setIsAdmin(false);
    setIsAffiliate(false);
  };

  const resetPassword = async (email: string) => {
    const redirectUrl = `${window.location.origin}/reset-password`;
    const { error } = await supabase.auth.resetPasswordForEmail(email, {
      redirectTo: redirectUrl,
    });
    return { error };
  };

  const updatePassword = async (password: string) => {
    const { error } = await supabase.auth.updateUser({ password });
    return { error };
  };

  const signInWithGoogle = async () => {
    const { error } = await supabase.auth.signInWithOAuth({
      provider: 'google',
      options: {
        redirectTo: `${window.location.origin}/`,
      },
    });
    return { error };
  };

  const authValue = useMemo(
    () => ({
      user,
      session,
      isAdmin,
      isAffiliate,
      adminCheckComplete,
      loading,
      signIn,
      signUp,
      signOut,
      resetPassword,
      updatePassword,
      signInWithGoogle,
      hasRole,
      getUserRoles,
    }),
    [user, session, isAdmin, isAffiliate, adminCheckComplete, loading]
  );

  return (
    <AuthContext.Provider value={authValue}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};
